<script setup lang="ts">
import NovaButton from "@/ui-kit/NovaButton/NovaButton.vue";
import NovaIcon from "@/ui-kit/NovaIcon/NovaIcon.vue";
import NovaSelectSearch from "@/ui-kit/NovaSelectSearch/NovaSelectSearch.vue";
import NovaCheckbox from "@/ui-kit/NovaCheckbox/NovaCheckbox.vue";
import NovaLabel from "@/ui-kit/NovaLabel/NovaLabel.vue";
import { BaseOption } from "@/types/Option";
import { RawOperationalLinkInstance } from "./useRawOperationalLinkFactory";
import { useOperationalExperiencesSearchQuery } from "./useOperationalExperiencesSearchQuery";
import { useOperationalExperienceOptionsQuery } from "./useOperationalExperienceOptionsQuery";
import { useResolvedHref } from "@/features/experience-shared/composables/useResolvedHref";

export interface Props {
  link: RawOperationalLinkInstance;
  selectedOperationalIds: Set<string>;
  readonly: boolean;
  dropdownLocked: boolean;
}

interface Events {
  (e: "remove"): void;
}

const props = defineProps<Props>();
defineEmits<Events>();

const { $t } = useNuxtApp();

const searchQuery = ref("");
const operationalIdRef = computed(() => props.link.data.value.operational_id || undefined);

// Search operational experiences by code
const { data: searchResults, isFetching: isSearching } = useOperationalExperiencesSearchQuery(searchQuery);

// Get options for the selected operational experience
const { data: availableOptions } = useOperationalExperienceOptionsQuery(operationalIdRef);

// Build select options from search results, filtering out already-selected operationals
const serviceCodeOptions = computed<BaseOption<string>[]>(() => {
  if (searchQuery.value.length < 4 || !searchResults.value) return [];
  return searchResults.value
    .filter(
      (op) =>
        !props.selectedOperationalIds.has(op.operational_id) ||
        op.operational_id === props.link.data.value.operational_id
    )
    .map((op) => ({
      label: `${op.code} - ${op.name}`,
      value: op.operational_id,
    }));
});

const showSearchHint = computed(() => searchQuery.value.length < 4);

const selectedOption = computed<BaseOption<string>[]>(() => {
  const data = props.link.data.value;
  if (!data.operational_id) return [];
  return [{ label: `${data.code} - ${data.name}`, value: data.operational_id }];
});

const operationalErrors = computed(() => {
  return props.link.errors.value?.properties?.operational_id?.errors ?? [];
});

const optionsErrors = computed(() => {
  return props.link.errors.value?.properties?.options?.errors ?? [];
});

function handleSelectOperational(option: BaseOption<string>) {
  const operational = searchResults.value?.find((op) => op.operational_id === option.value);
  if (!operational) return;

  props.link.setOperationalExperience(operational, availableOptions.value ?? []);
}

// When options become available after selecting an operational (async fetch),
// auto-select them if the link has no options yet
watch(availableOptions, (options) => {
  if (
    options &&
    options.length > 0 &&
    props.link.data.value.options.length === 0 &&
    props.link.data.value.operational_id
  ) {
    props.link.setOperationalExperience(
      {
        operational_id: props.link.data.value.operational_id,
        name: props.link.data.value.name,
        code: props.link.data.value.code,
      },
      options
    );
  }
});

const operationalLinkPath = computed(() => {
  const operationalId = props.link.data.value.operational_id;
  return operationalId ? `/opinoia/${operationalId}/operational/configuration` : "";
});
const operationalLinkHref = useResolvedHref(operationalLinkPath);
</script>

<template>
  <div class="rounded-lg border border-neutral-60 p-4" data-testid="raw-operational-link-card">
    <div class="flex items-baseline justify-between">
      <div class="flex items-baseline gap-2">
        <div class="flex items-center gap-0.5">
          <p class="font-bold text-sm">{{ $t("experience.operational-links.service_code.title") }}</p>
          <span v-if="!props.readonly" class="text-lg text-error-110">*</span>
        </div>
        <NovaLabel v-if="props.readonly" theme="green" size="sm" data-testid="raw-operational-link-done-badge">
          {{ $t("common.done") }}
        </NovaLabel>
        <a
          v-if="props.link.data.value.operational_id"
          :href="operationalLinkHref"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="raw-operational-link-visit"
          class="inline-flex self-center text-primary-100"
        >
          <NovaIcon name="external-link" :size="20" />
        </a>
      </div>
      <NovaButton
        variant="text"
        size="sm"
        :theme="props.readonly ? 'light' : 'primary'"
        :disabled="props.readonly"
        data-testid="raw-operational-link-remove"
        @click="$emit('remove')"
      >
        {{ $t("experience.raw.operational_links.remove") }}
        <NovaIcon name="trash" class="ml-2 inline-block" :size="18" />
      </NovaButton>
    </div>
    <div class="mt-3">
      <p class="mb-1 text-xs font-semibold text-text-80" data-testid="raw-operational-link-service-code-label">
        {{ $t("experience.operational-links.service_code.label") }}
      </p>
      <NovaSelectSearch
        :id="`operational-link-${props.link.data.value.operational_id || 'new'}`"
        :options="serviceCodeOptions"
        :selected="selectedOption"
        :placeholder="$t('experience.operational-links.service_code.placeholder')"
        :disabled="props.readonly || (props.dropdownLocked && !!props.link.data.value.operational_id)"
        :loading="isSearching"
        :class="{ 'border border-error-110 rounded-lg': operationalErrors.length }"
        :max-height="580"
        data-testid="raw-operational-link-select"
        @select:option="handleSelectOperational"
        @update:search-query="(value: string) => (searchQuery = value)"
      >
        <template v-if="showSearchHint" #empty>
          <p class="p-3 text-center text-sm text-text-80">
            {{ $t("experience.raw.operational_links.search_hint") }}
          </p>
        </template>
      </NovaSelectSearch>
      <p
        v-if="operationalErrors.length"
        class="mt-1 text-xs text-error-110"
        data-testid="raw-operational-link-service-code-error"
      >
        {{ $t("experience.raw.operational_links.service_code_required") }}
      </p>
    </div>
    <div
      v-if="availableOptions?.length"
      class="mt-2 rounded-lg border p-2"
      :class="optionsErrors.length ? 'border-error-110 bg-neutral-30' : 'border-neutral-60 bg-neutral-30'"
      data-testid="raw-operational-link-options"
    >
      <div class="mb-2 flex items-center gap-0.5">
        <p class="text-sm font-black">{{ $t("experience.operational-links.included_options.title") }}</p>
        <span v-if="!props.readonly" class="text-lg text-error-110">*</span>
      </div>
      <div class="flex flex-col gap-2 rounded-lg bg-white p-2">
        <NovaCheckbox
          v-for="option in availableOptions"
          :key="option.option_id"
          :label="`${option.code ? option.code + ' - ' : ''}${option.name}`"
          :value="option.option_id"
          :disabled="props.readonly"
          :status="props.link.isOptionSelected(option.option_id) ? 'checked' : 'unchecked'"
          data-testid="raw-operational-link-option-checkbox"
          @update:status="props.link.toggleOption(option)"
        />
        <p v-if="optionsErrors.length" class="text-xs text-error-110" data-testid="raw-operational-link-options-error">
          {{ $t("experience.raw.operational_links.options_required") }}
        </p>
      </div>
    </div>
  </div>
</template>
